import React, { useEffect, useState } from 'react';

import { useAuth } from '@opendoor/auth-fe';
import { Box, PressableBox, Row, Text } from '@opendoor/bricks-next';
import { getSessionExtras } from 'components/api';
import { AnimatePresence, motion } from 'framer-motion';
import { useObservability } from 'helpers/observability';
import { useHasActiveBuyerOffers } from 'helpers/useBuyerOffers';
import { useLeads } from 'helpers/useLeads';

import type { MenuConfig, NavigationProps } from '../../types';
import { boxVariants, navList } from '../menuAnimations';
import { MobileMenuList } from '../mobile/MobileMenuList';
import { MobileMenuSubMenu } from '../mobile/MobileMenuSubMenu';
import styles from './MenuContent.module.css';
import { MenuCTA } from './MenuCTA';

interface MenuContentProps extends NavigationProps, MenuConfig {
  isVisible: boolean;
  containerRef: React.RefObject<HTMLDivElement>;
  onAnimationComplete: (definition: any) => void;
  variant?: 'default' | 'glass';
}

export const MenuContent: React.FC<MenuContentProps> = ({
  isVisible,
  containerRef,
  onAnimationComplete,
  slug,
  menuPanelNavLinks,
  mainNavLinks,
  ctaLink,
  ctaTitle,
  megaNavData,
  variant = 'default',
}) => {
  const { authentication, logout, user: human } = useAuth();
  const { trackEvent } = useObservability();
  const isLoggedIn = authentication.state === 'authenticated';
  const leads = useLeads();
  const { hasActiveOffers } = useHasActiveBuyerOffers(true);
  const [hasResaleDashboard, setHasResaleDashboard] = useState(false);

  useEffect(() => {
    if (!human) {
      return;
    }
    getSessionExtras().then((extras) => {
      setHasResaleDashboard(extras.has_resale_closing_dashboard);
    });
  }, [human?.humanToken, human?.customerUuid]);

  const handleSignOut = () => {
    trackEvent('cta-click', 'account-signout', undefined, { categoryOverride: 'homepage' });
    logout();
  };

  const animate = isVisible ? 'visible' : 'hidden';
  const [openSubMenu, setOpenSubMenu] = useState<string | null>(null);

  const signOutContent = isLoggedIn ? (
    <Row justifyContent="center" py="$4" px="$3">
      <PressableBox
        onPress={handleSignOut}
        aria-label="Sign out"
        analyticsName="cosmos-account-menu-sign-out"
        display="inline-flex"
        alignItems="center"
      >
        <Text tag="span" typography="$bodyMedium" color="$contentTertiary">
          Sign out
        </Text>
      </PressableBox>
    </Row>
  ) : null;

  const menuFooter = (
    <Box
      display="block"
      borderTopWidth="$0dot5x"
      borderStyle="solid"
      borderTopColor="$borderPrimary"
      pt="$4"
    >
      {signOutContent}
      <MenuCTA ctaLink={ctaLink} ctaTitle={ctaTitle} animate={animate} />
    </Box>
  );

  return (
    <motion.div
      onAnimationComplete={onAnimationComplete}
      variants={boxVariants}
      initial="hidden"
      animate={isVisible ? 'visible' : 'hidden'}
      exit="hidden"
      tabIndex={-1}
      ref={containerRef}
      className={styles.menuContainer}
      data-variant={variant}
      role="dialog"
      aria-labelledby="dialog-main-menu"
      aria-modal="true"
    >
      <motion.ul animate={animate} variants={navList} className={styles.menuList} role="list">
        <AnimatePresence initial={false}>
          {megaNavData && openSubMenu ? (
            <MobileMenuSubMenu
              key="submenu"
              megaNavData={megaNavData}
              openSubMenu={openSubMenu}
              navList={navList}
              onBack={() => setOpenSubMenu(null)}
              footer={menuFooter}
            />
          ) : (
            <MobileMenuList
              key="mainmenu"
              megaNavData={megaNavData}
              menuPanelNavLinks={menuPanelNavLinks}
              mainNavLinks={mainNavLinks}
              slug={slug}
              onSubMenuOpen={(itemName) => setOpenSubMenu(itemName)}
              isLoggedIn={isLoggedIn}
              leads={leads}
              hasActiveOffers={hasActiveOffers}
              hasResaleDashboard={hasResaleDashboard}
              footer={menuFooter}
            />
          )}
        </AnimatePresence>
      </motion.ul>
    </motion.div>
  );
};
